import { ChevronDown, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { accentRailCardMuted, accentRailCardSubtle } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

// Call sites (CapabilityList group/chain rows, Rakenne EquipmentTile) each pick one fixed
// combination of card/rail/chevron treatment — never a mix — so one `kind` selects all three
// together instead of three independently-varying props.
const KIND_STYLE = {
  group: {
    container: accentRailCardSubtle,
    rail: "border-l-4",
    chevron: ChevronDown,
    rotate: "rotate-180",
    chevronPosition: "trailing",
  },
  chain: {
    container: accentRailCardMuted,
    rail: "border-l-[3px]",
    chevron: ChevronRight,
    rotate: "rotate-90",
    chevronPosition: "leading",
  },
} as const;

type AccentRailDisclosureProps = {
  open: boolean;
  onToggle: () => void;
  trigger: ReactNode;
  children?: ReactNode;
  kind: keyof typeof KIND_STYLE;
  // Fixed rail color shown regardless of open state (e.g. a per-equipment-type accent). Omit to
  // use the theme accent token, shown only while open.
  accentColor?: string;
  triggerClassName: string;
  chevronClassName: string;
  // Rendered beside the trigger, not inside the <button> — use this for Tallenna/Peruuta
  // (or any control that must stay clickable without toggling the row).
  actions?: ReactNode;
};

// Shared single-item disclosure/accordion row: owns the accent-rail card styling, the trigger
// button with `aria-expanded`, and the rotating chevron. Open state is controlled by the caller
// (a group list keeps at most one row open at a time), so this component never holds its own.
// Panel content should stay guarded by the caller's own `open &&` check — passing it as `children`
// still evaluates it on every render, this component only skips *mounting* it while closed.
export function AccentRailDisclosure({
  open,
  onToggle,
  trigger,
  children,
  kind,
  accentColor,
  triggerClassName,
  chevronClassName,
  actions,
}: AccentRailDisclosureProps) {
  const style = KIND_STYLE[kind];
  const Chevron = style.chevron;
  const chevron = (
    <Chevron
      className={cn(
        "shrink-0 text-muted-foreground transition-transform",
        open && style.rotate,
        chevronClassName,
      )}
    />
  );

  return (
    <div
      className={cn(
        style.container,
        style.rail,
        !accentColor && (open ? "border-l-accent" : "border-l-transparent"),
      )}
      style={accentColor ? { borderLeftColor: accentColor } : undefined}
    >
      <div className="flex items-stretch">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className={cn("flex min-w-0 flex-1 text-left", triggerClassName)}
        >
          {style.chevronPosition === "leading" && chevron}
          {trigger}
          {style.chevronPosition === "trailing" && chevron}
        </button>
        {actions}
      </div>
      {open && children}
    </div>
  );
}
